import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  IconButton,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  Popover,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ProImg1 from "../../../../assets/images/AgentStar.svg";
import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";
import CachedIcon from "@mui/icons-material/Cached";
import {
  KnowledgeBase,
  KnowledgeBaseCardMetaData,
} from "../../../Types/KnowledgeBase";
import { SettingsOutlined, Source } from "@mui/icons-material";
import variables from "../../../../assets/styles/variables.module.scss";
import {
  StartLoading,
  StopLoading,
} from "../../../Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { ShowError } from "../../../Store/MessageReducer/MsgActions";
import { DASHBOARD_KNOWLEDGE_BASE, KNOWLEDGE_BASE } from "../../../../Services/Endpoint";
import { formatDate } from "../../../utils/Common/Common";
import { getFileIcon } from "../../../utils/File/FileIcon";
import Confirm from "../../../Types/Confirm";
import ConfirmPopup from "../../ConfirmPopup";
import BaseMultipartInstance from "../../../../Services/AxiosInstance/BaseMultupartInstance";
import CustomFileIcon from "../../../../assets/images/CustomFileIcon.svg";
import CustomLinkIcon from "../../../../assets/images/linkIcon.svg";
import { initialData } from "./kwCardMetaDataInitial";
import { KnowledgeBaseTypeEnum } from "../../../Enum/KnowledgeBaseEnum";



interface MycomponentProps {
  knowledgeBase: KnowledgeBase;
  deleteFun: (knowledgeBase: KnowledgeBase) => void;
  editFun: (knowledgeBase: KnowledgeBase) => void;
}

const KnowledgeBaseCard: React.FC<MycomponentProps> = (props) => {
  const dispatch = useDispatch();
  const [knowledgeBasesSource, setKnowledgeBasesSource] = useState<KnowledgeBaseCardMetaData>(initialData);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });

  useEffect(() => {
    getKnowledgeBaaseStateInfo();
  }, [props.knowledgeBase]);

  const getKnowledgeBaaseStateInfo = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(
        `${DASHBOARD_KNOWLEDGE_BASE}?knowledge_base_id=${props.knowledgeBase.id}`
      ).then((res) => res?.data);
      const files_per_kb = data?.files_per_kb.length > 0 ? data.files_per_kb[0] : []
      const urls_per_kb = data?.urls_per_kb.length > 0 ? data.urls_per_kb[0] : []
      setKnowledgeBasesSource((preVal) => ({ ...preVal, files_per_kb, urls_per_kb }));
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const uploadSingleFile = (file: File, knowledgeId: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("description", "");
    formData.append("process_immediately", "false");
    return BaseMultipartInstance.post(
      `${KNOWLEDGE_BASE}/${knowledgeId}/upload`,
      formData
    );
  };

  const filesUploadKnowledgeBase = async (
    fileArr: File[],
    knowledgeId?: string
  ) => {
    try {
      dispatch(StartLoading());
      if (fileArr && fileArr.length > 0 && knowledgeId) {
        const uploadPromises = fileArr.map((file) =>
          uploadSingleFile(file, knowledgeId)
        );
        // uploadPromises.push(await BaseInstance.post(`${KNOWLEDGE_BASE}/${knowledgeId}/process-all`).then((res) => res?.data))
        await Promise.all(uploadPromises);
        await BaseInstance.post(
          `${KNOWLEDGE_BASE}/${knowledgeId}/process-all`
        ).then((res) => res?.data);
        getKnowledgeBaaseStateInfo();
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      const fileArr = Array.from(files);
      filesUploadKnowledgeBase(fileArr, props.knowledgeBase.id);
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());
      const data = await BaseInstance.delete(
        `${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/files/${fileId}`
      ).then((res) => res?.data);
      getKnowledgeBaaseStateInfo();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading());
    }
  };

  const confirmDelete = (fileId: string) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete your data? This action cannot be undone.",
      varient: "delete",
      functionName: deleteFile,
      functionParam: fileId,
    });
  };

  const [filesPopoverAnchor, setFilesPopoverAnchor] =
    useState<null | HTMLElement>(null);
  const [linksPopoverAnchor, setLinkPopoverAnchor] =
    useState<null | HTMLElement>(null);

  const handleFilesClick = (event: React.MouseEvent<HTMLElement>, type: string) => {
    if (knowledgeBasesSource.files_per_kb.file_count && knowledgeBasesSource.files_per_kb.file_count > 0 && type == "file") {
      setFilesPopoverAnchor(event.currentTarget);
    }
    if (knowledgeBasesSource.urls_per_kb.url_count && knowledgeBasesSource.urls_per_kb.url_count > 0 && type == "link") {
      setLinkPopoverAnchor(event.currentTarget);
    }
  };


  const handleFilesPopoverClose = () => setFilesPopoverAnchor(null);
  const handleLinkPopoverClose = () => setLinkPopoverAnchor(null);
  const filesPopoverOpen = Boolean(filesPopoverAnchor);
  const linkPopoverOpen = Boolean(linksPopoverAnchor);

  return (
    <Card
      sx={{
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(256, 256, 256, 0.7)",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {/* <Avatar
            src={ProImg1}
            sx={{
              backgroundColor: "#04DB93",
              "& img": {
                objectFit: "none",
              },
            }}
          /> */}
          {props.knowledgeBase.type === KnowledgeBaseTypeEnum.PDF ? (
            <Box
              sx={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#05DABB",
                borderRadius: "50%",
              }}
            >
              <DescriptionIcon sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <Box
              sx={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#05DABB",
                borderRadius: "50%",
              }}
            >
              <LanguageIcon sx={{ color: "#fff" }} />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexGrow: 1,
            }}
          >
            {props.knowledgeBase.name.length > 38 ? (
              <Tooltip title={props.knowledgeBase.name} placement="top-start">
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  noWrap
                  sx={{
                    maxWidth: 250,
                    lineHeight: "20px",
                    textTransform: "capitalize",
                  }}
                >
                  {props.knowledgeBase.name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="subtitle1"
                fontWeight={500}
                noWrap
                sx={{
                  maxWidth: 250,
                  lineHeight: "20px",
                  textTransform: "capitalize",
                }}
              >
                {props.knowledgeBase.name}
              </Typography>
            )}

            <Typography
              variant="caption"
              noWrap
              sx={{
                maxWidth: 250,
                color: "#6E6E6E",
                textTransform: "capitalize",
              }}
            >
              {props.knowledgeBase.updated_at
                ? props.knowledgeBase.updated_at
                  ? formatDate(props.knowledgeBase.updated_at)
                  : "--"
                : props.knowledgeBase.created_at
                  ? formatDate(props.knowledgeBase.created_at)
                  : "--"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Sources
          </Typography>
          <Box sx={{ mt: 1 }}>
            {/* Files row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.5}

            >
              <Box display="flex" alignItems="center" gap={1}
                onClick={(e) => handleFilesClick(e, "file")}
              >
                {/* <DescriptionIcon sx={{ color: "#757575", fontSize: 20 }} /> */}
                <img src={CustomFileIcon} alt="file" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="source-text"
                  sx={{
                    transition: "color 0.2s",
                    cursor: knowledgeBasesSource?.files_per_kb.file_count > 0 ? "pointer" : "default",
                    "&:hover .source-text": {
                      color: "#05DABB",
                    },
                  }}
                >
                  {knowledgeBasesSource.files_per_kb.file_count === 1
                    ? "1 File"
                    : `${knowledgeBasesSource.files_per_kb.file_count || 0} Files`}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.secondary"
                className="source-text"
                sx={{ transition: "color 0.2s" }}
              >

                {Math.round(knowledgeBasesSource.files_per_kb.file_size / 1024) || 0} KB
              </Typography>
            </Box>
            <Popover
              open={filesPopoverOpen}
              anchorEl={filesPopoverAnchor}
              onClose={handleFilesPopoverClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  minWidth: 250,
                  maxHeight: 210,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {
                  knowledgeBasesSource.files_per_kb.files?.map((file) => (
                    <Box
                      key={file.file_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {getFileIcon(file.file_name)}
                      <Tooltip title={file.file_name} placement="top-start">
                        <Typography
                          noWrap
                          sx={{ maxWidth: 220, minWidth: 220 }}
                        >
                          {file.file_name}
                        </Typography>
                      </Tooltip>
                      <IconButton
                        onClick={() => {
                          confirmDelete(file.file_id);
                          handleFilesPopoverClose();
                        }}
                        size="small"
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                }
              </Box>
            </Popover>
            {/* </Box> */}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1} onClick={(e) => handleFilesClick(e, "link")}>
                {/* <DescriptionIcon sx={{ color: "#757575", fontSize: 20 }} /> */}
                <img src={CustomLinkIcon} alt="file" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="source-text"

                  sx={{
                    transition: "color 0.2s",
                    cursor: knowledgeBasesSource?.urls_per_kb.url_count > 0 ? "pointer" : "default",
                    "&:hover .source-text": {
                      color: "#05DABB",
                    },
                  }}
                >
                  {knowledgeBasesSource.urls_per_kb.url_count === 1
                    ? "1 Link"
                    : `${knowledgeBasesSource.urls_per_kb.url_count || 0} Links`}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.secondary"
                className="source-text"
                sx={{ transition: "color 0.2s" }}
              >

                {Math.round(knowledgeBasesSource.urls_per_kb.url_size / 1024) || 0} KB
              </Typography>
              <Popover
                open={linkPopoverOpen}
                anchorEl={linksPopoverAnchor}
                onClose={handleLinkPopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    minWidth: 250,
                    maxHeight: 210,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {
                    knowledgeBasesSource.urls_per_kb.urls?.map((url, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <img src={CustomLinkIcon} alt="link" />
                        <Tooltip title={url.source_url} placement="top-start">
                          <Typography
                            noWrap
                            sx={{ maxWidth: 220, minWidth: 220 }}
                          >
                            {url.source_url}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))
                  }
                </Box>
              </Popover>
            </Box>
          </Box>

        </Box>

      </CardContent >
      <Divider sx={{ width: "100%" }} />

      {/* Actions */}
      <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={400} color="text.secondary">
            Characters Trained:
          </Typography>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            1074
          </Typography>
        </Box>
        <Box display="flex">
          <IconButton component="label" disabled={props.knowledgeBase.type !== KnowledgeBaseTypeEnum.PDF}>
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.txt"
              disabled={props.knowledgeBase.type !== KnowledgeBaseTypeEnum.PDF}
            />
            <FileUploadOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => getKnowledgeBaaseStateInfo()}>
            <CachedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => props.editFun(props.knowledgeBase)}>
            <SettingsOutlined sx={{ fontSize: "1rem" }} />
          </IconButton>
          <IconButton
            onClick={() => props.deleteFun(props.knowledgeBase)}
            title="Delete"
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
      <ConfirmPopup details={confirmPopupData} />
    </Card >
  );
};

export default KnowledgeBaseCard;
